/**
 * Account Overview Component
 * Displays balance, P&L, and portfolio metrics
 */

import { useAccount } from '../api/client';

export function AccountOverview() {
  const { data, isLoading, error } = useAccount();

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading account data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading account: {error.message}</div>
      </div>
    );
  }

  const account = data?.account;
  if (!account) return null;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Account Overview</h2>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {/* Balance */}
        <div className="metric">
          <div className="metric-label">Portfolio Value</div>
          <div className="metric-value">
            ${account.portfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Cash */}
        <div className="metric">
          <div className="metric-label">Cash Available</div>
          <div className="metric-value">
            ${account.cash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Buying Power */}
        <div className="metric">
          <div className="metric-label">Buying Power</div>
          <div className="metric-value">
            ${account.buyingPower.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Daily P&L */}
        <div className="metric">
          <div className="metric-label">Daily P&L</div>
          <div className={`metric-value ${account.dailyPnL >= 0 ? 'positive' : 'negative'}`}>
            {account.dailyPnL >= 0 ? '+' : ''}${account.dailyPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <span style={{ fontSize: '0.875rem', marginLeft: '0.5rem' }}>
              ({account.dailyPnLPct >= 0 ? '+' : ''}{account.dailyPnLPct.toFixed(2)}%)
            </span>
          </div>
        </div>

        {/* Weekly P&L */}
        <div className="metric">
          <div className="metric-label">Weekly P&L</div>
          <div className={`metric-value ${account.weeklyPnL >= 0 ? 'positive' : 'negative'}`}>
            {account.weeklyPnL >= 0 ? '+' : ''}${account.weeklyPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Monthly P&L */}
        <div className="metric">
          <div className="metric-label">Monthly P&L</div>
          <div className={`metric-value ${account.monthlyPnL >= 0 ? 'positive' : 'negative'}`}>
            {account.monthlyPnL >= 0 ? '+' : ''}${account.monthlyPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Open Positions */}
        <div className="metric">
          <div className="metric-label">Open Positions</div>
          <div className="metric-value">{account.openPositionsCount}</div>
        </div>

        {/* Portfolio Heat */}
        <div className="metric">
          <div className="metric-label">Portfolio Heat</div>
          <div className={`metric-value ${account.portfolioHeat > 60 ? 'negative' : ''}`}>
            {account.portfolioHeat.toFixed(1)}%
          </div>
        </div>

        {/* Last Updated */}
        <div className="metric">
          <div className="metric-label">Last Updated</div>
          <div className="metric-value" style={{ fontSize: '0.875rem' }}>
            {new Date(account.lastUpdated).toLocaleTimeString('en-US')}
          </div>
        </div>
      </div>
    </div>
  );
}
